import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "../../helpers/constants";
import { getCookie, signout } from "../../helpers/cookies";
import { usersParser, usersParserDropdown, usersParserHierarchy } from "./usersParser";

const customFetchBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = getCookie("magiclock_admin_token");
        if (token) {
            headers.set("Authorization", `BEARER ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await customFetchBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 403) {
        signout(() => {
            window.location.href = "/login";
        });
    }

    return result;
};

const usersApi = createApi({
    reducerPath: "usersApi",
    baseQuery: baseQueryWithReauth,
    keepUnusedDataFor: 0,
    tagTypes: ["Users"],
    endpoints: (builder) => ({
        getUsersList: builder.query({
            query: (params) => ({
                url: "/users",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => usersParser(response),
            providesTags: ["Users"],
            keepUnusedDataFor: 5,
        }),
        addNewUser: builder.mutation({
            query: (userData) => ({
                url: "/users",
                method: "POST",
                body: userData,
            }),
            invalidatesTags: ["Users"],
            keepUnusedDataFor: 5,
        }),
        updateUser: builder.mutation({
            query: (userData) => ({
                url: `/users/${userData.id}`,
                method: "PUT",
                body: userData,
            }),
            invalidatesTags: ["Users"],
            keepUnusedDataFor: 5,
        }),
        deleteUser: builder.mutation({
            query: (userId) => ({
                url: `/users/${userId}`,
                method: "DELETE",
            }),
            transformResponse: (response, meta, arg) => {
                // If the response indicates dependencies exist, return the dependency info
                if (response.success === 409) {
                    return {
                        canDelete: false,
                        dependencies: response.result,
                        message: response.message,
                    };
                }
                return {
                    canDelete: true,
                    message: response.message,
                };
            },
            transformErrorResponse: (response) => {
                // Handle 409 conflict (dependencies exist)
                if (response.status === 409) {
                    return {
                        canDelete: false,
                        dependencies: response.data?.result,
                        message: response.data?.message,
                    };
                }
                return response;
            },
            invalidatesTags: ["Users"],
            keepUnusedDataFor: 5,
        }),
        checkUserDependencies: builder.query({
            query: (userId) => `/users/${userId}/dependencies`,
            providesTags: ["Users"],
            keepUnusedDataFor: 5,
        }),
        getUserById: builder.query({
            query: (userId) => `/users/${userId}`,
            providesTags: ["Users"],
            keepUnusedDataFor: 5,
        }),
        getUsersDropdown: builder.query({
            query: (params) => ({
                url: "/users/dropdown",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => usersParserDropdown(response),
            providesTags: ["Users"],
            keepUnusedDataFor: 5,
        }),
        getUsersHierarchy: builder.query({
            query: () => ({
                url: "/users/hierarchy/tree",
                method: "GET",
            }),
            transformResponse: (response) => usersParserHierarchy(response),
            providesTags: ["Users"],
            keepUnusedDataFor: 5,
        }),
        resendWhatsAppMessage: builder.mutation({
            query: ({ userId, password }) => ({
                url: `/users/${userId}/resend-whatsapp`,
                method: "POST",
                body: { userId, password },
            }),
            invalidatesTags: ["Users"],
            keepUnusedDataFor: 5,
        }),
    }),
});

export const {
    useGetUsersListQuery,
    useAddNewUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useCheckUserDependenciesQuery,
    useGetUserByIdQuery,
    useGetUsersDropdownQuery,
    useGetUsersHierarchyQuery,
    useResendWhatsAppMessageMutation,
} = usersApi;
export default usersApi;
